import { JsonApiDocument, JsonApiResource } from 'json-api-models';
import { omit } from 'lodash-es';

// A response can reflect the server before the client last changed a field,
// such as a slow list that includes a model which was just updated. Each
// field's pending write and latest confirmed write are recorded here, with
// the run of the request that made them. Responses skip fields with a pending
// write, or a write confirmed after their run started by another run. A run
// confirms its write before its own response is synced, so it takes its own
// response's values.
//
// A write waits for the field's pending write to settle before it is sent, as
// updates often replace a whole value and the server could otherwise save an
// older value over a newer one.
//
// Runs are identified by when they started, noted against their fetch
// options, which VueUse creates for each run and passes through every hook,
// unlike the context objects it copies.
type Field = { pending?: Promise<void>; confirmedAt?: number; confirmedStartedAt?: number };

const fieldsByModel = new Map<string, Map<string, Field>>();
const startTimes = new WeakMap<object, number>();
let clock = 0;

/**
 * Record a run's write of a resource's fields to a model.
 *
 * Returns a promise for the fields' pending writes settling, and functions to
 * confirm the write once the server has saved it, to abandon it if the server
 * didn't, and to list the attributes and relationships it is still pending
 * for.
 */
export function recordWrite(
    model: { type: string; id: string },
    resource: Pick<JsonApiResource, 'attributes' | 'relationships'>,
    context: { options: object },
) {
    let settle!: () => void;
    const settled = new Promise<void>((resolve) => (settle = resolve));

    const key = `${model.type}:${model.id}`;
    const modelFields = fieldsByModel.get(key) || new Map<string, Field>();
    fieldsByModel.set(key, modelFields);

    const fieldsFor = (names: string[]) =>
        names.map((name) => {
            const field = modelFields.get(name) || {};
            modelFields.set(name, field);
            return [name, field] as const;
        });
    const attributes = fieldsFor(Object.keys(resource.attributes || {}));
    const relationships = fieldsFor(Object.keys(resource.relationships || {}));
    const fields = [...attributes, ...relationships].map(([, field]) => field);

    const earlier = fields.flatMap((field) => field.pending ?? []);
    fields.forEach((field) => (field.pending = settled));

    const release = () => {
        settle();
        fields.forEach((field) => field.pending === settled && (field.pending = undefined));
    };
    const pending = (section: typeof attributes) =>
        section.filter(([, field]) => field.pending === settled).map(([name]) => name);

    return {
        earlier: Promise.all(earlier),
        confirm() {
            const confirmedAt = ++clock;
            const confirmedStartedAt = startTimes.get(context.options);
            fields.forEach((field) => Object.assign(field, { confirmedAt, confirmedStartedAt }));
            release();
        },
        abandon: release,
        pendingFields() {
            return { attributes: pending(attributes), relationships: pending(relationships) };
        },
    };
}

/** Note when a run of a request starts, given its fetch context. */
export function markStarted(context: { options: object }) {
    startTimes.set(context.options, ++clock);
}

/**
 * Carry a run's start time over to the response data it received.
 *
 * Each result of an atomic operations response is synced as a document of its
 * own, so it is marked too.
 */
export function markReceived(context: { options: object }, data: unknown) {
    if (!data || typeof data !== 'object') return;

    const startedAt = startTimes.get(context.options)!;
    const results = (data as { 'atomic:results'?: unknown[] })['atomic:results'] || [];
    for (const document of [data, ...results]) {
        if (document && typeof document === 'object') startTimes.set(document, startedAt);
    }
}

/**
 * Remove fields from a response document that would overwrite newer writes.
 *
 * Fields are removed while a write to them is pending, or if another run's
 * write to them was confirmed after the document's run started. Documents
 * from no recorded run, such as the initial payload, only lose fields with a
 * pending write.
 */
export function withoutStaleFields(document: JsonApiDocument): JsonApiDocument {
    if (!fieldsByModel.size) return document;

    const startedAt = startTimes.get(document) ?? Infinity;

    const fresh = (resource: JsonApiResource): JsonApiResource => {
        const modelFields = fieldsByModel.get(`${resource.type}:${resource.id}`);
        if (!modelFields) return resource;

        const stale: string[] = [];
        for (const [name, field] of modelFields) {
            const { pending, confirmedAt, confirmedStartedAt } = field;
            if (pending || (confirmedStartedAt !== startedAt && confirmedAt! > startedAt)) {
                stale.push(name);
            }
        }

        if (!stale.length) return resource;

        return {
            ...resource,
            attributes: omit(resource.attributes, stale),
            relationships: omit(resource.relationships, stale),
        };
    };

    return {
        ...document,
        data: Array.isArray(document.data)
            ? document.data.map(fresh)
            : document.data && fresh(document.data),
        included: document.included?.map(fresh),
    };
}
